import React, { useEffect, useState } from 'react';
import { css } from 'aphrodite';
import { Col, Row } from 'antd';
import { toUpper } from 'lodash';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import TextBase from '../../common/text';
import { Colors } from '../../themes/Colors';
import PageTitleHeader from '../../common/page-title-header';
import ButtonGradient from '../../common/button-gradient';
import {
  MESSAGE_TYPE,
  getCreditCardTypeString,
  messageManager,
  translate
} from '../../helper';
import { SVGImageComponentReact } from '../../themes/Images';
import { ACHDetail } from '../../common/ach/ACHDetail';
import { CardForm } from '../../common/card-form';
import { ConfirmModal } from '../../common/confirm-modal';
import { useDeletePaymentMethod, useGetPaymentMethod } from '../../hooks/usePayment';
import { styles } from './styles';
import { paymentAPIService } from "../../services/paymentAPIService";
import CustomSpinner from '../../common/CustomSpinner';

const VALUES = {
  bank_name: "",
  account_type: "",
  account_holder_type: "",
  last4: "",
  routing_number: "",
  account_holder_name: "",
  email: "",
};

const CARD_VALUES = {
  card_holder: '',
  number: '',
  expiration_date: '',
  cvc: ''
};


export const PaymentMethodEditBase = ({ card, goBack, title = "editCard" }) => {
  const { paymentMethod, isError, error } = useGetPaymentMethod({
    id: card?.djstripe_id
  });
  const { deletePaymentMethod, isDeletingPaymentMethod } = useDeletePaymentMethod(onSuccess);
  const {setDefaultPaymentMethod} = paymentAPIService;

  const [values, setValues] = useState(VALUES);
  const [cardValues, setCardValues] = useState(CARD_VALUES);
  const [validation, setValidation] = useState({});
  const [showDelete, setShowDelete] = useState(false);
  const [showDefaultConfirm,setShowDefaultConfirm] = useState(false);
  const [isSettingDefaultPaymentMethod,setIsSettingDefaultPaymentMethod] = useState(false);

  useEffect(() => {
    handleSetDetails();
  }, [paymentMethod]);

  const showCancelConfirm = () => {
    setShowDelete(true);
  };

  const closeCancelConfirm = () => {
    setShowDelete(false);
  };

  const handleSetDetails = () => {
    if (!paymentMethod?.djstripe_id) return;
    if (paymentMethod?.type === 'card') {
      const card = paymentMethod?.card || {};
      const cv = {
        expiration_date: card.exp_month + '/ ' + card.exp_year,
      brand: getCreditCardTypeString(card?.brand),
      number: '**** **** **** ' + card?.last4,
      card_holder: paymentMethod?.billing_details?.name || '',
      cvc: '***'
      };
      setCardValues(cv);
    } else {
      const values = {
        bank_name: paymentMethod?.us_bank_account?.bank_name || '',
        account_type: paymentMethod?.us_bank_account?.account_type || '',
        account_holder_type: paymentMethod?.us_bank_account?.account_holder_type || '',
        last4: paymentMethod?.us_bank_account?.last4 || '',
        routing_number: paymentMethod?.us_bank_account?.routing_number || '',
        account_holder_name: paymentMethod?.billing_details?.name || '',
        email: paymentMethod?.billing_details?.email || '',
      };

      setValues(values);
    }
  };

  const handleDelete = () => {
    deletePaymentMethod({ djstripe_id: paymentMethod?.djstripe_id });
  };

  const showDeleteConfirm = () => {
    showCancelConfirm();
  };

  function onSuccess(data) {
    closeCancelConfirm();
    messageManager({
      messageShow: translate('cardDeleted'),
      type: MESSAGE_TYPE.success
    });
    goBack();
  }

  const closeDefaultConfirm = () => {
    setShowDefaultConfirm(false);
  }

  const handleSetDefaultPayment = async () => {
    setIsSettingDefaultPaymentMethod(true);
    try {
      await setDefaultPaymentMethod({ id: paymentMethod?.djstripe_id });
      messageManager({
        messageShow: translate('defaultPaymentMethodUpdated'),
        type: MESSAGE_TYPE.success
      });
      goBack()
    } catch (e) {
      messageManager({
        messageShow: translate('errorSettingDefaultPaymentMethod'),
        type: MESSAGE_TYPE.error
      });
    }finally{
      setIsSettingDefaultPaymentMethod(false);
      closeDefaultConfirm();
    }
  }

  return (
    <Elements style={{ width: '100%', height: 'calc(var(--fs-hero)*2.35849)' }} stripe={loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)}>
      <div className={css(styles.container)}>
        <Row justify="space-between">
          <Col>
            <Row>
              <div
                style={{ marginRight: 'calc(var(--fs-xl)*1.5566039)' }}
                className="flex-row-center-center cursor-pointer"
                onClick={goBack}>
                <SVGImageComponentReact.IconArrowBack style={{width:"var(--fs-lg)", height:"var(--fs-lg)", color: '#1A5A78'}}/>
              </div>
              <PageTitleHeader
                title={translate(title)}
                showBar={false}
              />
            </Row>
          </Col>
        </Row>

        {!paymentMethod?.type ? 
          <CustomSpinner fullPage />
          :
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}>
            <div
              style={{
                width: 'calc(var(--fs-hero)*9.4339622)',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                backgroundColor: 'white',
                padding: 'calc(var(--sp-lg)*1.5094339)',
                border: '1px solid rgba(128, 128, 128, 0.2)',

              }}>
              {paymentMethod?.type === 'card' ? (
                <div style={{ width: '100%' }}>
                  <CardForm
                    values={cardValues}
                    setValues={setValues}
                    validation={validation}
                    setValidation={setValidation}
                    brand={cardValues?.brand}
                    disabled
                  />
                </div>
              ) : paymentMethod?.type === 'us_bank_account' ? (
                <ACHDetail
                  values={values}
                  setValues={setValues}
                  validation={validation}
                  setValidation={setValidation}
                  brand={values?.brand}
                  disabled
                />
              ):(<></>
              )}

              {isError && (
                <TextBase fontWeight={400} size={'calc(var(--fs-xs)*1.32075)'} color={Colors.red}>
                  {error?.response?.data?.detail || 'Error while adding card'}
                </TextBase>
              )}

              {typeof paymentMethod?.is_default === 'boolean' && !paymentMethod?.is_default && (
                <>
                  <ButtonGradient
                    onClick={()=>setShowDefaultConfirm(true)}
                    textStyle={{ fontWeight: 700, fontSize: 'var(--fs-md2)', }}
                    title={toUpper(translate("setAsDefault"))}
                    style={{ marginTop: 'calc(var(--sp-md2)*1.69811)', width: '100%' }}
                  />
                  <ButtonGradient
                    onClick={showDeleteConfirm}
                    textStyle={{ fontWeight: 200, fontSize: 'calc(var(--fs-xs)*1.60377)',color: '#ff4d4f' }}
                    title={toUpper(translate("delete"))}
                    backgroundColor="#fff"
                    style={{ marginTop: "calc(var(--sp-md2)*1.69811)", width: "100%", backgroundColor: "#fff",border:'1px solid #ff4d4f' }}
                  />
                </>
              )}

            </div>
          </div>
        }

        <ConfirmModal
          visible={showDelete}
          handleOk={handleDelete}
          status="danger"
          message="You are deleting your card"
          okBtnText="DELETE"
          cancelBtnText="CANCEL"
          isLoading={isDeletingPaymentMethod}
          width="calc(var(--fs-hero)*9.4339622)"
          handleCancel={closeCancelConfirm}
        />
        <ConfirmModal
          visible={showDefaultConfirm}
          handleOk={handleSetDefaultPayment}
          status="warning"
          message="You are setting this card as default"
          okBtnText="SET DEFAULT"
          cancelBtnText="CANCEL"
          isLoading={isSettingDefaultPaymentMethod}
          width="calc(var(--fs-hero)*9.4339622)"
          handleCancel={closeDefaultConfirm}
        />
      </div>
    </Elements>
  );
}
